import React from 'react';
import Helmet from 'react-helmet';
import { graphql, useStaticQuery } from 'gatsby';

interface PageMetaDataProps {
  title: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  pagePath?: string;
}

const PageMetaData: React.FC<PageMetaDataProps> = ({ title, description, image, imageAlt, pagePath }) => {
  const { site } = useStaticQuery(graphql`
    query PageMetaDataQuery {
      site {
        siteMetadata {
          title
          siteUrl
        }
      }
    }
  `);

  const siteTitle = site.siteMetadata.title;
  const siteUrl = site.siteMetadata.siteUrl;
  const pageTitle = `${title} | ${siteTitle}`;
  const pageUrl = pagePath ? `${siteUrl}${pagePath}` : siteUrl;
  const imageUrl = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : null;
  const socialDescription = description || title;

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{pageTitle}</title>
      <link rel="canonical" href={pageUrl} />
      {description ? <meta name="description" content={description} /> : null}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:description" content={socialDescription} />
      <meta name="twitter:card" content={imageUrl ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={socialDescription} />
      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
      {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
      {imageAlt ? <meta property="og:image:alt" content={imageAlt} /> : null}
      {imageAlt ? <meta name="twitter:image:alt" content={imageAlt} /> : null}
    </Helmet>
  );
};

export default PageMetaData;
